import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Megaphone, Lifebuoy, CaretRight, CaretLeft } from "@phosphor-icons/react";
import AllianceChat from "@/components/AllianceChat";
import PremiumBadge from "@/components/PremiumBadge";
import RankIcon from "@/components/RankIcon";
import { EMERGENCY_TYPES } from "@/lib/gameConfig";

// Per-share mission timer. Reads `all_on_scene_at` (set server-side by
// `_autoresolve_share_if_due` the moment combined coverage meets the call's
// requirements) and `onSceneDurationSec`. Pre-WORKING the badge reads
// "PENDING UNITS"; while working it counts down + draws a slim progress bar.
function MissionTimer({ share }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);
  const onSceneDur = share?.call?.onSceneDurationSec || 0;
  if (!onSceneDur) return null;
  const startedIso = share.all_on_scene_at;
  if (!startedIso) {
    return (
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 leading-tight" data-testid={`mission-timer-${share.id}`}>
        Awaiting full response
      </div>
    );
  }
  let startedAt;
  try {
    startedAt = new Date(startedIso).getTime();
  } catch (_e) { return null; }
  const elapsed = (Date.now() - startedAt) / 1000;
  const remaining = Math.max(0, Math.ceil(onSceneDur - elapsed));
  const pct = Math.max(0, Math.min(100, 100 - (elapsed / onSceneDur) * 100));
  return (
    <div className="mt-0.5 leading-tight" data-testid={`mission-timer-${share.id}`}>
      <div className="flex items-center gap-1.5">
        <div className="h-1 bg-white/5 overflow-hidden flex-1">
          <div
            className="h-full bg-emerald-400 transition-[width] duration-500 ease-linear"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="mono text-[9px] font-bold text-emerald-400 shrink-0">{remaining}s</span>
      </div>
    </div>
  );
}

function ago(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

// Returns the still-needed unit counts for a shared call after subtracting all
// units that are currently responding (host + helpers, en-route or on-scene
// — returning units don't count). For OR-groups we pick the option whose
// remaining total is lowest so the helper sees the cheapest path to closing
// the call. Returns:
//   { ok: true }                       → every required option is fully met
//   { ok: false, remaining: {Engine:1} } → these unit-types are still needed
function computeStillNeeded(share) {
  const call = share?.call || {};
  const primary = call.requiredUnits || {};
  const alts = Array.isArray(call.alternates) ? call.alternates : [];
  const options = [primary, ...alts].filter((o) => o && Object.keys(o).length);
  if (options.length === 0) return { ok: true, remaining: {} };

  // Tally every non-returning unit responding to this call.
  const tally = {};
  const addUnit = (u) => {
    if (!u || u.returning) return;
    const t = u.apparatusType || "Engine";
    tally[t] = (tally[t] || 0) + 1;
  };
  (share.requester_units || []).forEach(addUnit);
  (share.helpers || []).forEach(addUnit);

  const subtract = (opt) => {
    const out = {};
    let total = 0;
    Object.entries(opt).forEach(([k, v]) => {
      const need = Math.max(0, v - (tally[k] || 0));
      if (need > 0) {
        out[k] = need;
        total += need;
      }
    });
    return { remaining: out, total };
  };
  const scored = options.map(subtract);
  // Any option fully met → call is fully responded to (work timer or close)
  if (scored.some((s) => s.total === 0)) return { ok: true, remaining: {} };
  // Otherwise show the cheapest still-open option
  scored.sort((a, b) => a.total - b.total);
  return { ok: false, remaining: scored[0].remaining };
}

/**
 * HelpRequestsPanel — full-height right slide-out for Mutual Aid.
 * Renders a thin vertical tab when collapsed; otherwise shows the alliance
 * roster, map toggles, and active shared calls (with respond / close buttons).
 *
 * Props:
 *   open / onToggle             — collapsible (controlled by parent + localStorage)
 *   alliance, shares            — current alliance + open shared-call list
 *   showAllianceStations/Units  — map overlay toggles
 *   onOpenMutualAid             — opens the create/join dialog
 *   onRespond, onCloseShare     — share actions
 */
export default function HelpRequestsPanel({
  open,
  onToggle,
  alliance,
  shares,
  currentUserId,
  onRespond,
  onOpenShare,
  onCloseShare,
  onOpenMutualAid,
  showAllianceStations,
  onToggleAllianceStations,
  showAllianceUnits,
  onToggleAllianceUnits,
  // When true, the panel is being rendered inside a mobile drawer so the
  // absolute-positioned floating tab + sidebar wrappers should be swapped
  // for a flex-fill layout.
  mobile = false,
}) {
  // ---------- Collapsed: thin always-visible vertical tab (desktop only) ----------
  if (!open && !mobile) {
    const pending = shares?.length || 0;
    return (
      <button
        type="button"
        data-testid="mutual-aid-toggle-open"
        onClick={onToggle}
        title="Open Mutual Aid"
        className="absolute top-[60px] right-0 bottom-56 w-8 z-40 border-l border-white/10 bg-[#0F1115] hover:bg-[#161A20] transition-colors flex flex-col items-center justify-start pt-3 gap-2"
      >
        <Lifebuoy size={14} className="text-orange-400" />
        <span
          className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-400"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          MUTUAL AID
        </span>
        {pending > 0 && (
          <span
            data-testid="mutual-aid-pending-count"
            className="mono text-[9px] font-bold tabular-nums bg-orange-500 text-black px-1.5 py-0.5"
            style={{ minWidth: 18, textAlign: "center" }}
          >
            {pending > 99 ? "99+" : pending}
          </span>
        )}
        <CaretLeft size={12} className="text-zinc-500 mt-1" />
      </button>
    );
  }

  // ---------- Expanded: full panel ----------
  // In mobile mode the wrapping <MobilePanelDrawer> already provides the
  // full-screen container + header, so we collapse to a fill-style wrapper.
  const containerCls = mobile
    ? "h-full w-full flex flex-col"
    : "absolute top-[60px] right-0 bottom-56 w-80 border-l border-white/10 bg-[#0F1115] z-40 flex flex-col";

  // No alliance yet — show "Find a Group" prompt + collapse toggle in header
  if (!alliance) {
    return (
      <aside className={containerCls} data-testid="help-requests-panel-empty">
        <PanelHeader title="Mutual Aid" onToggle={onToggle} />
        <div className="flex-1 flex items-center justify-center p-4 text-center">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              Not in a Mutual Aid Group
            </div>
            <Button
              onClick={onOpenMutualAid}
              size="sm"
              data-testid="empty-open-mutual-aid-btn"
              className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
            >
              Find a Group
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  // Alliance present — full panel
  return (
    <aside className={containerCls} data-testid="help-requests-panel">
      <PanelHeader
        title={alliance.name}
        right={
          <span className="mono text-[9px] text-zinc-500" data-testid="help-shares-count">
            {shares.length} active
          </span>
        }
        onToggle={onToggle}
      />
      <div className="px-3 py-2 border-b border-white/5 flex flex-col gap-1.5 shrink-0">
        <label className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 cursor-pointer">
          <input
            data-testid="toggle-alliance-stations"
            type="checkbox"
            checked={!!showAllianceStations}
            onChange={onToggleAllianceStations}
            className="accent-emerald-500"
          />
          Show Mutual Aid Group facilities
        </label>
        <label className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 cursor-pointer">
          <input
            data-testid="toggle-alliance-units"
            type="checkbox"
            checked={!!showAllianceUnits}
            onChange={onToggleAllianceUnits}
            className="accent-emerald-500"
          />
          Show Mutual Aid units on my calls
        </label>
        <Button
          data-testid="manage-mutual-aid-btn"
          onClick={onOpenMutualAid}
          size="sm"
          variant="outline"
          className="rounded-none h-7 mono uppercase text-[10px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5 mt-1"
        >
          Manage Group
        </Button>
      </div>
      {/* Upper half: shared help requests */}
      <div
        className="flex-1 overflow-y-auto min-h-0 basis-1/2"
        data-testid="help-shares-list"
      >
        {shares.length === 0 ? (
          <div className="p-4 text-center mono text-[10px] text-zinc-600 uppercase tracking-widest">
            — no help requests —
          </div>
        ) : (
          shares.map((s) => {
            const isMine = s.requester_id === currentUserId;
            const callTypeKey = s.call?.type || "incident";
            // Always prefer the EMERGENCY_TYPES label so e.g. type key
            // "car_accident" displays as "INJURY ACCIDENT" — not the raw key
            // with underscores swapped for spaces.
            const callLabel =
              EMERGENCY_TYPES[callTypeKey]?.label || callTypeKey.replace(/_/g, " ");
            const upgradedLabel = s.call?.upgradedFrom
              ? (EMERGENCY_TYPES[s.call.upgradedFrom]?.label ||
                  s.call.upgradedFrom.replace(/_/g, " "))
              : null;
            const addr = s.call?.address || "";
            const upgradedFrom = s.call?.upgradedFrom;
            const helperCount = (s.helpers || []).length;
            // What still needs to be dispatched (excludes units already on
            // scene / en route from anyone). Recomputed every time `shares`
            // updates (polled every 5 s from the parent).
            const stillNeeded = computeStillNeeded(s);
            return (
              <div
                key={s.id}
                data-testid={`help-share-${s.id}`}
                role="button"
                tabIndex={0}
                onClick={() => onOpenShare && onOpenShare(s)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && onOpenShare) {
                    e.preventDefault();
                    onOpenShare(s);
                  }
                }}
                className="border-b border-white/5 px-2 py-1 cursor-pointer hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5 relative"
                style={{ borderLeft: `3px solid ${isMine ? "#FF3B30" : "#FF9F0A"}` }}
                title="Click to view full call details and dispatch specific units"
              >
                {/* Row 1: requester · upgraded badge · close (host) / quick-send (helper) */}
                <div className="flex items-center gap-1 leading-tight">
                  <Megaphone size={10} className={`shrink-0 ${isMine ? "text-red-400" : "text-orange-400"}`} />
                  {s.requester_is_premium && <PremiumBadge size={10} />}
                  <RankIcon rank={s.requester_rank} size={10} />
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-400 truncate">
                    {s.requester_name}{isMine ? " (you)" : ""}
                  </span>
                  <span className="mono text-[9px] text-zinc-500 shrink-0">· {ago(s.created_at)}</span>
                  {upgradedFrom && (
                    <span
                      data-testid={`upgraded-badge-${s.id}`}
                      title={`Upgraded from ${upgradedLabel}`}
                      className="mono text-[8px] uppercase tracking-widest px-1 border border-red-500/60 text-red-300 bg-red-500/10 shrink-0"
                    >
                      ↑
                    </span>
                  )}
                  <div className="ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                    {isMine ? (
                      <button
                        type="button"
                        data-testid={`close-share-${s.id}`}
                        onClick={() => onCloseShare(s.id)}
                        title="Close this help request"
                        className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-zinc-300 hover:bg-white/10"
                      >
                        Close
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`respond-share-${s.id}`}
                        onClick={() => onRespond(s)}
                        title="Auto-send nearest available unit"
                        className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-orange-500 hover:bg-orange-400 text-black"
                      >
                        +1 Send
                      </button>
                    )}
                  </div>
                </div>
                {/* Row 2: call label */}
                <div className="text-[12px] font-semibold uppercase leading-tight truncate" title={callLabel}>
                  {callLabel}
                </div>
                {/* Row 3: address */}
                <div className="text-[10px] text-zinc-400 truncate leading-tight" title={addr}>{addr}</div>
                {s.note && (
                  <div className="mono text-[9px] text-amber-300 leading-tight truncate" title={s.note}>
                    "{s.note.slice(0, 80)}"
                  </div>
                )}
                {/* Row 4: still-needed pills (only when present) */}
                <div className="mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`still-needed-${s.id}`}>
                  {stillNeeded.ok ? (
                    <span
                      data-testid={`still-needed-met-${s.id}`}
                      className="mono text-[9px] uppercase tracking-widest px-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                    >
                      ✓ ALL RESPONDING
                    </span>
                  ) : (
                    Object.entries(stillNeeded.remaining).map(([k, v]) => (
                      <span
                        key={k}
                        data-testid={`still-needed-pill-${s.id}-${k}`}
                        className="mono text-[9px] uppercase tracking-widest px-1 border border-orange-400/60 text-orange-300 bg-orange-500/10"
                      >
                        {k.slice(0, 3).toUpperCase()} {v}
                      </span>
                    ))
                  )}
                  {/* Patient-flow indicators (mutually exclusive in meaning):
                      MED pill   → unassessed patients on scene AND no Medic
                                   is en route or on scene yet (host + helpers).
                                   Tells alliance members a Medic needs to be
                                   dispatched.
                      Pink "!"   → at least one assessed patient is awaiting
                                   the player to pick a hospital destination. */}
                  {(() => {
                    const sp = Array.isArray(s.patients) ? s.patients : [];
                    if (sp.length === 0) return null;
                    const unassessed = sp.filter(
                      (p) => !p.assessed && p.needsTransport !== false
                    ).length;
                    const awaitingTransport = sp.filter(
                      (p) => p.assessed && p.needsTransport && (p.transportStatus || "none") === "none"
                    ).length;
                    // Any Medic / HEMS en-route or on-scene from anyone in the alliance?
                    const medicResponding = [
                      ...(s.requester_units || []),
                      ...(s.helpers || []),
                    ].some(
                      (u) => {
                        const t = u?.apparatusType || "";
                        return (t === "Medic" || t === "HEMS") && !u?.returning;
                      }
                    );
                    return (
                      <>
                        {unassessed > 0 && !medicResponding && (
                          <span
                            data-testid={`share-needs-medic-${s.id}`}
                            title={`${unassessed} patient${unassessed > 1 ? "s" : ""} on scene — no Medic responding yet`}
                            className="mono text-[9px] uppercase tracking-widest px-1 border border-pink-400/60 text-pink-200 bg-pink-500/15"
                          >
                            MED {unassessed}
                          </span>
                        )}
                        {awaitingTransport > 0 && (
                          <span
                            data-testid={`share-needs-transport-${s.id}`}
                            title={`${awaitingTransport} patient${awaitingTransport > 1 ? "s" : ""} ready — click Transport to pick a hospital`}
                            className="mono text-[10px] font-bold text-white bg-pink-500 border border-pink-300 px-1 py-px animate-pulse leading-none shrink-0"
                            style={{ boxShadow: "0 0 8px rgba(236,72,153,0.7)" }}
                          >
                            !
                          </span>
                        )}
                      </>
                    );
                  })()}
                  {helperCount > 0 && (
                    <span className="mono text-[9px] uppercase tracking-widest px-1 bg-emerald-500/20 text-emerald-300">
                      +{helperCount}
                    </span>
                  )}
                </div>
                <MissionTimer share={s} />
              </div>
            );
          })
        )}
      </div>
      {/* Lower half: alliance chat */}
      <div className="basis-1/2 min-h-0 flex flex-col">
        <AllianceChat allianceId={alliance.id} currentUserId={currentUserId} />
      </div>
    </aside>
  );
}

function PanelHeader({ title, right, onToggle }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 shrink-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <Lifebuoy size={13} className="text-orange-400 shrink-0" />
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 truncate">
          {title}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {right}
        <button
          type="button"
          data-testid="mutual-aid-toggle-close"
          onClick={onToggle}
          title="Collapse Mutual Aid"
          className="ml-1 mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-1.5 py-0.5"
        >
          <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
}
